import {
  createCollection,
  localStorageCollectionOptions,
  useLiveQuery,
} from "@tanstack/react-db";
import { z } from "zod";
import { LANGUAGES, type Language } from "@/lib/languages";
import { PREFIX, SINGLETON_KEY } from "./storage-keys";

const storageKey = `${PREFIX}-user-prefs`;

const schema = z.object({
  username: z.string(),
  audioAuthorization: z.boolean().optional(),
  successSoundEnabled: z.boolean().default(true),
  language: z.enum(LANGUAGES).default("en"),
});

const collection = createCollection(
  localStorageCollectionOptions({
    id: "user-prefs",
    storageKey,
    getKey: () => SINGLETON_KEY,
    schema,
  }),
);

export const useUserPrefs = () => {
  const { data } = useLiveQuery((q) => q.from({ userPrefs: collection }));
  return data[0] ?? null;
};

export const RESERVED_USERNAMES = ["error"];

export const saveUsername = (username: string) => {
  const trimmed = username.trim();
  if (RESERVED_USERNAMES.includes(trimmed)) return;

  const existing = collection.state.get(SINGLETON_KEY);
  if (existing) {
    collection.update(SINGLETON_KEY, (draft) => {
      draft.username = trimmed;
    });
    return;
  }
  collection.insert({
    username: trimmed,
    successSoundEnabled: true,
  });
};

export const saveAudioAuthorization = (value: boolean) => {
  const existing = collection.state.get(SINGLETON_KEY);
  if (existing) {
    collection.update(SINGLETON_KEY, (draft) => {
      draft.audioAuthorization = value;
    });
  }
};

export const saveSuccessSoundEnabled = (value: boolean) => {
  const existing = collection.state.get(SINGLETON_KEY);
  if (existing) {
    collection.update(SINGLETON_KEY, (draft) => {
      draft.successSoundEnabled = value;
    });
  }
};

export const saveLanguage = (language: Language) => {
  const existing = collection.state.get(SINGLETON_KEY);
  if (existing) {
    collection.update(SINGLETON_KEY, (draft) => {
      draft.language = language;
    });
  }
};

export const userPrefsStore = {
  storageKey,
  schema,
};
